import { parseScanPayload } from './gs1';
import type { WedgeBurstResult } from './wedgeAssembler';

/**
 * A record of what each scanner actually sent, kept so that "I pulled the trigger and nothing
 * happened" can be answered on the device instead of guessed at.
 *
 * A handheld gives no feedback of its own beyond its beep, and a payload the app understands but
 * cannot act on — a pack's linear barcode, which carries a GTIN but no serial number — looks
 * from the operator's side exactly like a scan that never arrived. These are different problems
 * with different fixes, and telling them apart needs the bytes.
 */

const DEBUG_PREFERENCE = 'rxerp.scanner.debug';
const DEBUG_QUERY_PARAM = 'scandebug';
const MAX_ENTRIES = 25;

export type ScanDiagnosticEntry = {
  id: number;
  at: number;
  source: 'camera' | 'hardware';
  /** Exactly what the scanner sent, before any parsing. */
  raw: string;
  /** How the app read it: the AIs it found, or that it found none. */
  summary: string;
  /** Set when the app understood the payload but no screen could act on it. */
  note: string | null;
  /** Set when the burst never became a scan at all. */
  rejected: string | null;
};

let entries: ScanDiagnosticEntry[] = [];
let nextId = 1;
const listeners = new Set<(entries: ScanDiagnosticEntry[]) => void>();

const readPreference = () => {
  try {
    return window.localStorage.getItem(DEBUG_PREFERENCE);
  } catch {
    return null;
  }
};

/**
 * On by `?scandebug=1` in the URL, which is remembered afterwards so it survives the navigation
 * the very next scan performs. `?scandebug=0` turns it off again.
 */
export const isScanDebugEnabled = (): boolean => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(DEBUG_QUERY_PARAM);
    if (fromUrl !== null) {
      const enabled = fromUrl !== '0' && fromUrl !== 'off';
      window.localStorage.setItem(DEBUG_PREFERENCE, enabled ? 'on' : 'off');
      return enabled;
    }
  } catch {
    /* a URL or storage we cannot read simply means the default */
  }
  return readPreference() === 'on';
};

export const setScanDebugEnabled = (enabled: boolean) => {
  try {
    window.localStorage.setItem(DEBUG_PREFERENCE, enabled ? 'on' : 'off');
  } catch {
    /* applied for this session even if it cannot be persisted */
  }
};

export const getScanDiagnostics = () => entries;

export const subscribeScanDiagnostics = (
  listener: (entries: ScanDiagnosticEntry[]) => void
) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const clearScanDiagnostics = () => {
  entries = [];
  listeners.forEach((listener) => listener(entries));
};

const publish = (entry: Omit<ScanDiagnosticEntry, 'id' | 'at'>) => {
  entries = [{ ...entry, id: nextId, at: Date.now() }, ...entries].slice(0, MAX_ENTRIES);
  nextId += 1;
  listeners.forEach((listener) => listener(entries));
};

/** Control characters shown as their names, so a missing separator is visible rather than absent. */
export const visualisePayload = (raw: string) =>
  raw
    .replace(/\u001d/g, '⟨GS⟩')
    .replace(/\u001e/g, '⟨RS⟩')
    .replace(/\u0004/g, '⟨EOT⟩')
    .replace(/\r/g, '⟨CR⟩')
    .replace(/\n/g, '⟨LF⟩')
    .replace(/\t/g, '⟨TAB⟩');

/**
 * What the app made of a payload, and — the part that matters here — why a screen might do
 * nothing with it. A pack's linear barcode parses perfectly and still cannot be picked, because
 * the serial number only exists in the 2D DataMatrix beside it.
 */
const describe = (raw: string) => {
  const payload = parseScanPayload(raw);
  if (payload.kind === 'raw') {
    return {
      summary: 'not a GS1 barcode — treated as a zone or tote label',
      note: 'Screens that want a product barcode will ignore this.'
    };
  }
  const found = Object.keys(payload.ais)
    .sort()
    .map((ai) => `(${ai})`)
    .join(' ');
  const summary = `${payload.kind.toUpperCase()} ${found}${
    payload.separatorsMissing ? ' — separators missing, boundaries inferred' : ''
  }`;

  if (!payload.serialNumber && payload.gtin) {
    return {
      summary,
      note:
        'GTIN but no serial number (21). This is the linear barcode on the pack — the serial is ' +
        'only in the 2D DataMatrix. Screens that pick or verify serialised items ignore it.'
    };
  }
  if (!payload.serialNumber) {
    return { summary, note: 'No serial number, so serialised screens ignore it.' };
  }
  return { summary, note: null };
};

/** Records a payload the provider published (or tried to). */
export const recordScan = (raw: string, source: 'camera' | 'hardware') => {
  const { summary, note } = describe(raw);
  publish({ source, raw, summary, note, rejected: null });
};

const REJECTION_TEXT: Record<string, string> = {
  empty: 'nothing was captured — the scan did not reach the app',
  'too-short': 'too few characters, and no Enter or Tab to vouch for them',
  duplicate: 'identical to the previous scan within 600 ms'
};

/**
 * Records the listener being wired up. Re-attaching happens on every navigation to a scanning
 * screen, and a trigger pull that lands in the gap goes nowhere at all — so a timeline of
 * attaches next to the scans is what tells "the scan never arrived" from "the app was not
 * listening yet".
 */
export const recordScannerAttached = ({
  captureField,
  suppressingKeyboard
}: {
  captureField: boolean;
  suppressingKeyboard: boolean;
}) => {
  // Whether this browser implements the attribute we rely on to keep the keyboard down. If it
  // does not, the keyboard will open however the field is configured, and the answer is the
  // device's input method rather than anything here — worth knowing without a test cycle.
  const policySupported =
    typeof HTMLElement !== 'undefined' && 'virtualKeyboardPolicy' in HTMLElement.prototype;
  publish({
    source: 'hardware',
    raw: '',
    summary: `listener attached — capture field ${
      captureField ? 'created' : 'not used (camera mode, or iOS)'
    }${captureField && suppressingKeyboard ? ', inputmode=none' : ''}${
      captureField
        ? `, keyboard policy ${policySupported ? 'supported' : 'NOT supported by this browser'}`
        : ''
    }`,
    note:
      captureField && suppressingKeyboard
        ? 'inputmode=none stops a wedge delivering scans as a string. Set rxerp.hardwareScanner.suppressKeyboard to "off".'
        : null,
    rejected: null
  });
};

let lastIgnoredAt = -Infinity;

/**
 * Records key events the listener stepped aside from because something editable had focus.
 *
 * That rule is deliberate — a barcode aimed at the manual-entry box belongs to that box — but
 * from the operator's side it is indistinguishable from scanning being broken, and the same
 * focused element is what asks Android for a keyboard. Throttled, since a scan is a burst.
 */
export const recordKeysIgnored = (target: Element | null) => {
  const now = Date.now();
  if (now - lastIgnoredAt < 1000) return;
  lastIgnoredAt = now;
  const tag = target?.tagName || 'unknown';
  const id = target && (target as HTMLElement).id ? `#${(target as HTMLElement).id}` : '';
  publish({
    source: 'hardware',
    raw: '',
    summary: `keys ignored — ${tag}${id} has focus`,
    note: 'The scanner stands aside for anything editable so manual entry keeps working. Whatever holds focus here is both stopping the scan and raising the keyboard.',
    rejected: null
  });
};

/** Records a camera that would not start, so the panel explains a blank preview. */
export const recordCameraFailure = (reason: string) => {
  publish({
    source: 'camera',
    raw: '',
    summary: 'camera did not start',
    note: `${reason}. Handheld scanning is unaffected — if the trigger also does nothing, the problem is not the camera.`,
    rejected: null
  });
};

/** Records a handheld burst that never became a scan — otherwise it leaves no trace at all. */
export const recordRejectedBurst = (result: WedgeBurstResult) => {
  if (!result.rejected) return;
  publish({
    source: 'hardware',
    raw: result.data,
    summary: `burst via ${result.source}, closed by ${result.terminator}`,
    note: null,
    rejected: REJECTION_TEXT[result.rejected] || result.rejected
  });
};
